/**
 * useAskThreads.js — assistant conversations, kept for a day.
 *
 * WHY THEY ARE SAVED AT ALL
 *
 * The first version held the thread in React state and nowhere else, so a
 * refresh, a tab close, or a tap on Notifications lost it. Someone builds up a
 * comparison over four turns, goes to check something, comes back to an empty
 * box — and learns not to use the assistant for anything that took effort.
 *
 * WHY THEY EXPIRE
 *
 * Because of what ends up in them. People paste clinical notes into a text box
 * despite being told not to, and a permanent conversation log is a growing pile
 * of text nobody decided to store. Twenty-four hours is long enough to finish a
 * piece of work and short enough that the pile never accumulates.
 *
 * TWO THINGS DELETE THEM, deliberately.
 *
 *   1. A scheduled function, every hour. This is the one that makes the promise
 *      true, because it is in the repository and deploys with everything else.
 *   2. The query below, which filters on expiresAt. A thread that outlives its
 *      purge — the job failed, the schedule was paused — still disappears from
 *      the UI on time rather than quietly reappearing a week later.
 *
 * Belt and braces, and the braces are the part a console setting cannot undo.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  collection, doc, query, where, orderBy, limit,
  getDocs, setDoc, deleteDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

/** Must match the rule's 25-hour ceiling, with room to spare. */
export const THREAD_TTL_HOURS = 24;

/** Must match `turns.size() <= 20` in firestore.rules. */
export const MAX_THREAD_TURNS = 20;

/**
 * Stored answers are capped below what the server will return.
 *
 * The server allows 20,000 characters; twenty of those on one document is
 * 400KB, which is under Firestore's 1MiB limit but not by a margin worth
 * relying on. Trimming the stored copy costs nothing a reader will notice —
 * the full answer is on screen; this is the scrollback.
 */
const MAX_STORED_ANSWER = 12000;

const threadsRef = (uid) => collection(db, 'users', uid, 'askThreads');

/** Newest first, expired ones excluded whether or not the purge has run. */
export function useAskThreads(uid) {
  const [threads, setThreads] = useState([]);
  const [status, setStatus] = useState('loading');
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!uid) { setThreads([]); setStatus('ready'); return undefined; }
    let alive = true;

    getDocs(query(
      threadsRef(uid),
      where('expiresAt', '>', Timestamp.now()),
      orderBy('expiresAt', 'desc'),
      limit(20),
    ))
      .then((snap) => {
        if (!alive) return;
        // Ordered by expiresAt because Firestore requires the first orderBy to
        // match the range filter's field. Since every thread gets the same TTL
        // from its last write, that ordering IS recency — a thread touched more
        // recently expires later.
        setThreads(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus('ready');
      })
      .catch((err) => {
        // A thread list that will not load must not stop someone asking a new
        // question. The page renders without the list.
        console.error('Could not load saved conversations', err);
        if (alive) { setThreads([]); setStatus('error'); }
      });

    return () => { alive = false; };
  }, [uid, nonce]);

  return { threads, status, refresh };
}

export function useAskThreadActions(uid) {
  /**
   * Write the whole thread, every turn.
   *
   * Not an arrayUnion append: the turns list is trimmed to the last twenty, and
   * appending cannot trim. Rewriting a document of at most a few hundred
   * kilobytes once per answer — against a call that just took several seconds —
   * is not a cost worth optimising away.
   *
   * The expiry is extended on every write, so an active conversation stays
   * alive for a day from its LAST message rather than its first. A thread you
   * are still using disappearing mid-sentence would be the worst version of
   * this feature.
   */
  const saveThread = useCallback(async (threadId, turns) => {
    if (!uid || !threadId || !turns?.length) return null;

    const trimmed = turns.slice(-MAX_THREAD_TURNS).map((t) => ({
      q: String(t.question ?? '').slice(0, 2000),
      a: String(t.answer ?? '').slice(0, MAX_STORED_ANSWER),
    }));

    const expiresAt = Timestamp.fromMillis(Date.now() + THREAD_TTL_HOURS * 3600 * 1000);

    try {
      await setDoc(doc(threadsRef(uid), threadId), {
        // The opening question is the title. It is what the person typed, so it
        // is what they will recognise in a list — better than any summary a
        // model could write, and free.
        title: trimmed[0].q.slice(0, 200),
        turns: trimmed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt,
      }, { merge: true });
      return threadId;
    } catch (err) {
      /**
       * Swallowed on purpose.
       *
       * The answer is already on screen. Interrupting someone reading it to say
       * the scrollback did not save helps nobody, and the failure is recoverable
       * by simply asking again. It is logged, not surfaced.
       */
      console.error('Could not save that conversation', err);
      return null;
    }
  }, [uid]);

  const removeThread = useCallback(async (threadId) => {
    if (!uid || !threadId) return;
    await deleteDoc(doc(threadsRef(uid), threadId));
  }, [uid]);

  return { saveThread, removeThread };
}

/** A stable id for a new conversation. Firestore's, so it cannot collide. */
export function newThreadId(uid) {
  return doc(threadsRef(uid)).id;
}

/** "3 hours left" — what a reader needs to know about a thread that expires. */
export function threadTimeLeft(expiresAt) {
  const ms = (expiresAt?.toMillis?.() ?? 0) - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.round(ms / 60000))}m left`;
}
